<template>
  <VChart class="chart-container" :option="heatmapOption" :notMerge="true" autoresize style="width: 100%; height: 100%;" />
</template>

<script>
import { defineComponent, computed } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { HeatmapChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
  TitleComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import { detection_zone_palettes } from '@/constants.js'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatWeekday } from '@/i18n/format'

use([
  HeatmapChart,
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
  TitleComponent,
  CanvasRenderer
])

export default defineComponent({
  name: 'HeatmapDetectionZone',
  components: { VChart },
  props: {
    heatmapChartData: {
      type: Object,
      required: true
    },
    timeSpan: {
      type: String,
      required: true
    },
    detectionZoneTypeName: {
      type: String,
      required: true
    },
    // What one cell counts. Defaults to the historical behaviour ("Visitors") so
    // every existing caller is unaffected; the service-response panel passes its
    // own label so the tooltip doesn't claim to be counting visitors.
    seriesName: {
      type: String,
      default: ''   // empty → the translated "Visitors"
    },
    // Layout for a SMALL panel. The default grid takes 80% of the height, which is
    // right at the 520-640px the visitor heatmap gets and collides with the legend
    // at ~300px. Compact shrinks the plot, lifts the legend clear and drops the axis
    // name (there is no room for it, and the panel header already says the period).
    compact: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { t } = useI18n()
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    // Shown day names: English as above; Dutch "maandag" … (1 Jan 2024 was a Monday).
    const dayLabels = () => currentLanguage() === 'en'
      ? days
      : days.map((_, i) => formatWeekday(new Date(2024, 0, 1 + i)))
    const isEn = () => currentLanguage() === 'en'

    const palette = computed(() => {
      return detection_zone_palettes[ props.detectionZoneTypeName ] ?? {
        heatmap: [                                   // ← YOU NEED THIS
         'rgba(230,230,230,0.2)',
         'rgba(200,200,200,0.4)',
         'rgba(150,150,150,0.6)',
         'rgba(100,100,100,0.8)'
        ]
      }
    })

    const hourLabels = computed(() => {
      const { minHour, maxHour } = props.heatmapChartData
      const labels = []
      for (let h = minHour; h <= maxHour; h++) {
        const padded = h.toString().padStart(2, '0')
        labels.push(`${padded}:00`)
      }
      return labels
    })

    const transformedData = computed(() => {
      const matrix = props.heatmapChartData.heatmap_matrix || []
      const data = []
      for (let dayIdx = 0; dayIdx < matrix.length; dayIdx++) {
        for (let hourIdx = 0; hourIdx < matrix[dayIdx].length; hourIdx++) {
          const value = matrix[dayIdx][hourIdx]
          data.push([hourIdx, dayIdx, value || '-']) // ECharts expects [x, y, value]
        }
      }
      return data
    })

    // recompute whenever heatmapChartData changes
   const maxValue = computed(() => {
     const matrix = props.heatmapChartData.heatmap_matrix || []
     const all = matrix.flat().filter(v => typeof v === 'number')
     return all.length > 0 ? Math.max(...all) : 10
   })

   // if you also want a dynamic min:
   const minValue = computed(() => {
     const matrix = props.heatmapChartData.heatmap_matrix || []
     const all = matrix.flat().filter(v => typeof v === 'number')
     return all.length > 0 ? Math.min(...all) : 0
   })

    const heatmapOption = computed(() => ({
      tooltip: isEn()
        ? { position: 'top' }
        // Dutch numbers read 1.234; English keeps ECharts' own formatting.
        : { position: 'top', valueFormatter: v => { const n = Array.isArray(v) ? v[2] : v; return typeof n === 'number' ? formatNumber(n) : n } },
      grid: props.compact
        ? { height: '62%', top: '6%', left: '13%', right: '4%' }
        : { height: '80%', top: '5%', left: '15%' },
      xAxis: {
        type: 'category',
        data: hourLabels.value,
        axisLabel: props.compact ? { fontSize: 10, interval: 1 } : {},
        splitArea: { show: true }
      },
      yAxis: {
        type: 'category',
        name: props.compact ? '' : `${props.timeSpan}`,
        nameTextStyle: { align: 'left' },
        axisLabel: props.compact ? { fontSize: 10 } : {},
        data: dayLabels(),
        splitArea: { show: true }
      },
      visualMap: {
        min: minValue.value || 0,
        max: maxValue.value || 100,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: props.compact ? '4%' : '0%',
        itemWidth: props.compact ? 12 : 20,
        itemHeight: props.compact ? 80 : 140,
        textStyle: props.compact ? { fontSize: 10 } : {},
        ...(isEn() ? {} : { formatter: v => formatNumber(v) }),
        inRange: {
          color: palette.value.heatmap
        }
      },
      series: [
        {
          name: props.seriesName || t('charts.heatmap.visitors'),
          type: 'heatmap',
          data: transformedData.value,
          label: { show: false },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    }))

    return { heatmapOption }
  }
})
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
}
</style>